import React, { useEffect, useState } from 'react';
import { Input } from '@cotera/client/v0/components/ui/input';
import { Button } from '@cotera/client/v0/components/ui/button';
import { Text } from '@cotera/client/app/components/ui';
import { toast } from '@cotera/client/app/components/ui';
import { useQueryClient } from '@tanstack/react-query';
import { ResourceType } from '@cotera/api';
import { PersistedWorkflow } from '@cotera/sdk/core';
import { useTenantedClient, useWhoami } from '@cotera/client/app/stores/org';
import { queryKeys } from '@cotera/client/app/hooks/query-cache-keys';
import type { WorkflowEventTriggerViewData } from '../types';
import { useWorkflowGraphMutations } from '../../../use-workflow-graph-mutations';
import { useWorkflowModel } from '../../../workflow.model';
import { draftUpdateEventTrigger } from '../../../workflow-draft-graph';
import { WorkflowIdempotencyKeyEditor } from '../../workflow-idempotency-key-editor';
import { useEmitEventNameSuggestions } from '../../../hooks/use-emit-event-name-suggestions';

const { PersistedWorkflowTriggerKind } = PersistedWorkflow;

export const EventTriggerEditor: React.FC<{
  workflowId: string;
  bindingId: string;
  trigger: WorkflowEventTriggerViewData;
  onSaved?: () => void;
}> = ({ workflowId, bindingId, trigger, onSaved }) => {
  const model = useWorkflowModel();
  const mutations = useWorkflowGraphMutations();
  const client = useTenantedClient();
  const queryClient = useQueryClient();
  const orgId = useWhoami((s) => s.org.id);

  const [name, setName] = useState(trigger.name);
  const [eventName, setEventName] = useState(trigger.eventName);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setName(trigger.name);
    setEventName(trigger.eventName);
  }, [trigger.id, trigger.name, trigger.eventName]);

  const isPersistedOnServer = model
    .getBaselineDraftState()
    .graph.triggers.some(
      (snapshot) =>
        snapshot.trigger.id === trigger.id &&
        snapshot.kind === PersistedWorkflowTriggerKind.Event
    );

  const trimmedName = name.trim();
  const trimmedEventName = eventName.trim();
  const canSave = trimmedName !== '' && trimmedEventName !== '';

  const eventNameSuggestions = useEmitEventNameSuggestions();
  const listId = `event-trigger-names-${trigger.id}`;

  const handleSave = async (): Promise<void> => {
    if (!canSave) {
      return;
    }
    setSaving(true);

    const patch = {
      bindingId,
      name: trimmedName,
      eventName: trimmedEventName,
    };

    if (!isPersistedOnServer) {
      const saved = await mutations.updateEventTrigger(patch);
      setSaving(false);
      if (!saved) {
        return;
      }
      toast.success('Event trigger updated');
      onSaved?.();
      return;
    }

    const result = await client.resource.workflow.updateTrigger({
      workflowId,
      bindingId,
      triggerId: trigger.id,
      name: trimmedName,
      eventName: trimmedEventName,
    });
    if (result.isErr()) {
      setSaving(false);
      toast.error(
        result.error.t === 'application'
          ? result.error.err.message
          : 'Failed to update event trigger'
      );
      return;
    }

    const idempotencyResult = await model.persistIdempotencyKey(
      model.getIdempotencyKey()
    );
    if (!idempotencyResult.success) {
      setSaving(false);
      toast.error(idempotencyResult.message);
      return;
    }

    await mutations.updateEventTrigger(patch);
    model.updateBaselineDraftState((state) =>
      draftUpdateEventTrigger(state, patch)
    );
    void queryClient.invalidateQueries({
      queryKey: queryKeys.resource.get({
        orgId,
        type: ResourceType.Workflow,
        id: workflowId,
      }),
    });
    setSaving(false);
    toast.success('Event trigger updated');
    onSaved?.();
  };

  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <Text.Caption className="text-muted-foreground block">
          Name
        </Text.Caption>
        <Input
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="Name this event trigger"
        />
      </div>
      <div className="space-y-1.5">
        <Text.Caption className="text-muted-foreground block">
          Event name
        </Text.Caption>
        <Input
          value={eventName}
          list={eventNameSuggestions.length > 0 ? listId : undefined}
          placeholder="order.created"
          onChange={(event) => setEventName(event.target.value)}
        />
        {eventNameSuggestions.length > 0 ? (
          <datalist id={listId}>
            {eventNameSuggestions.map((suggestion) => (
              <option key={suggestion} value={suggestion} />
            ))}
          </datalist>
        ) : null}
      </div>
      <WorkflowIdempotencyKeyEditor
        value={model.getIdempotencyKey()}
        onChange={(next) => model.setIdempotencyKey(next)}
        disabled={saving}
      />
      <Button
        type="button"
        className="w-full"
        disabled={saving || !canSave}
        onClick={() => void handleSave()}
      >
        Save event trigger
      </Button>
    </div>
  );
};
